/**
 * The obligation chain — the single source of truth for what you are allowed to
 * play. This module is imported unchanged by both the client (to grey out cards
 * with zero latency) and the server (which refuses illegal plays regardless).
 *
 * Belgian Manille, in order:
 *   1. Follow suit. Absolute — no exemption, ever.
 *   2. Holding the led suit and able to beat the master in it → you must beat it.
 *   3. Void in the led suit and holding trump → you must trump.
 *   4. An opponent already trumped and you can overtrump → you must overtrump.
 *   5. You cannot overtrump → you must still throw a trump (Belgian strictness).
 *   6. Every obligation except (1) is lifted while your partner is master.
 *
 * In a *zonder troef* deal `trump` is `null`: rules 3–5 are vacuous and only
 * follow-suit and the in-suit overtake survive.
 */

import { type Card, type Suit, cardStrength, cardSuit } from './cards.js';
import { type Trick, ledSuit, masterPlay, partnerOf, seatToMove } from './trick.js';
import { type VariantId, type VariantRules, resolveVariant } from './variant.js';

export type IllegalReason =
  | { readonly code: 'must-follow-suit'; readonly suit: Suit }
  | { readonly code: 'must-overtake'; readonly suit: Suit; readonly master: Card }
  | { readonly code: 'must-trump'; readonly led: Suit; readonly trump: Suit }
  | { readonly code: 'must-overtrump'; readonly master: Card; readonly trump: Suit }
  | { readonly code: 'must-trump-losing'; readonly master: Card; readonly trump: Suit }
  | { readonly code: 'not-in-hand' }
  | { readonly code: 'not-your-turn' }
  | { readonly code: 'not-playing' };

export interface MoveRuling {
  readonly card: Card;
  readonly legal: boolean;
  readonly reason?: IllegalReason;
}

/**
 * Every card in `hand` with a verdict and, when illegal, the one obligation
 * that forbids it. `legalMoves` is a thin filter over this, so a greyed-out
 * card and the reason shown on tap can never disagree.
 */
export function moveRulings(
  hand: readonly Card[],
  trick: Trick,
  trump: Suit | null,
  variant: VariantId | VariantRules,
): MoveRuling[] {
  const rules = resolveVariant(variant);
  const led = ledSuit(trick);

  // Leading: anything goes.
  if (led === null) return hand.map((card) => ({ card, legal: true }));

  const master = masterPlay(trick, trump);
  if (!master) return hand.map((card) => ({ card, legal: true }));

  const mover = seatToMove(trick);
  const partnerIsMaster = rules.partnerMasterExempts && master.seat === partnerOf(mover);
  const masterCard = master.card;
  const masterIsTrump = trump !== null && cardSuit(masterCard) === trump;

  const inLed = hand.filter((card) => cardSuit(card) === led);
  const trumps = trump === null ? [] : hand.filter((card) => cardSuit(card) === trump);

  // Stage 1 — which suit(s) may I play at all?
  let stage1: readonly Card[] = hand;
  let reason1: IllegalReason | null = null;

  if (inLed.length > 0) {
    // (1) Follow suit. Applies even when the partner is master.
    stage1 = inLed;
    reason1 = { code: 'must-follow-suit', suit: led };
  } else if (!partnerIsMaster && trumps.length > 0 && trump !== null) {
    if (!masterIsTrump) {
      // (3) Nobody has trumped yet.
      if (rules.mustTrumpWhenVoid) {
        stage1 = trumps;
        reason1 = { code: 'must-trump', led, trump };
      }
    } else {
      const higherTrumps = trumps.filter(
        (card) => cardStrength(card) > cardStrength(masterCard),
      );
      if (higherTrumps.length > 0 && rules.mustOvertrumpWhenVoid) {
        // (4) An opponent trumped and I can beat it.
        stage1 = higherTrumps;
        reason1 = { code: 'must-overtrump', master: masterCard, trump };
      } else if (higherTrumps.length > 0 && rules.mustTrumpWhenVoid) {
        stage1 = trumps;
        reason1 = { code: 'must-trump', led, trump };
      } else if (higherTrumps.length === 0 && rules.mustTrumpEvenIfLosing) {
        // (5) I cannot beat it but must still throw a trump.
        stage1 = trumps;
        reason1 = { code: 'must-trump-losing', master: masterCard, trump };
      }
    }
  }

  // Stage 2 — inside the led suit, must I beat the master?
  let stage2: readonly Card[] = stage1;
  let reason2: IllegalReason | null = null;

  if (inLed.length > 0 && !partnerIsMaster) {
    // A plain-suit card cannot beat a trump, so the obligation only exists
    // while the master is still winning in the led suit.
    const beatable = led === trump || !masterIsTrump;
    const obligation =
      led === trump ? rules.mustOvertrumpWhenTrumpLed : rules.mustOvertakeInSuit;
    if (beatable && obligation) {
      const higher = inLed.filter((card) => cardStrength(card) > cardStrength(masterCard));
      if (higher.length > 0) {
        stage2 = higher;
        reason2 =
          led === trump && trump !== null
            ? { code: 'must-overtrump', master: masterCard, trump }
            : { code: 'must-overtake', suit: led, master: masterCard };
      }
    }
  }

  const allowed = new Set(stage2);
  const followed = new Set(stage1);
  return hand.map((card) => {
    if (allowed.has(card)) return { card, legal: true };
    const reason = followed.has(card) ? reason2 : reason1;
    // Unreachable: a narrowed stage always carries its reason.
    return { card, legal: false, reason: reason ?? { code: 'not-in-hand' } };
  });
}

/**
 * The exact signature the whole app is built around: pure, no I/O, no clock.
 * Returns the cards the player to move may legally play, in hand order.
 */
export function legalMoves(
  hand: readonly Card[],
  trick: Trick,
  trump: Suit | null,
  variant: VariantId | VariantRules,
): Card[] {
  const legal: Card[] = [];
  for (const ruling of moveRulings(hand, trick, trump, variant)) {
    if (ruling.legal) legal.push(ruling.card);
  }
  return legal;
}

export function illegalReason(
  card: Card,
  hand: readonly Card[],
  trick: Trick,
  trump: Suit | null,
  variant: VariantId | VariantRules,
): IllegalReason | null {
  if (!hand.includes(card)) return { code: 'not-in-hand' };
  const ruling = moveRulings(hand, trick, trump, variant).find((r) => r.card === card);
  return ruling && !ruling.legal ? (ruling.reason ?? { code: 'not-in-hand' }) : null;
}

export function isLegalMove(
  card: Card,
  hand: readonly Card[],
  trick: Trick,
  trump: Suit | null,
  variant: VariantId | VariantRules,
): boolean {
  return illegalReason(card, hand, trick, trump, variant) === null;
}
